import { useCallback, useEffect, useRef, useState } from "react";

import {
  useEndPlaybackSessionMutation,
  useRefreshPlaybackUrlMutation,
  useSendHeartbeatMutation,
  useStartPlaybackSessionMutation,
} from "@/redux/service";
import { endPlaybackSessionOnUnload } from "@/redux/certifications/keepalive";
import {
  PlaybackProgress,
  PlaybackSessionTracker,
  UsePlaybackSessionOptions,
} from "@/domains/certifications/types";
import { HEARTBEAT_REJECT_REASONS } from "@/domains/certifications/constants";

const URL_REFRESH_LEAD_MS = 30_000;
const MIN_URL_REFRESH_DELAY_MS = 60_000;
const URL_REFRESH_RETRY_MS = 5_000;
const MAX_URL_REFRESH_ATTEMPTS = 5;
const HEARTBEAT_RETRY_MS = 5_000;
const MAX_HEARTBEAT_RETRY_MS = 60_000;
const RESUME_END_MARGIN_SECONDS = 5;

export const usePlaybackSession = ({
  certificationSlug,
  contentItemId,
}: UsePlaybackSessionOptions) => {
  const [startSession, { isError }] = useStartPlaybackSessionMutation();
  const [sendHeartbeat] = useSendHeartbeatMutation();
  const [refreshUrl] = useRefreshPlaybackUrlMutation();
  const [endSession] = useEndPlaybackSessionMutation();

  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const [tracker, setTracker] = useState<PlaybackSessionTracker | null>(null);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [progress, setProgress] = useState<PlaybackProgress | null>(null);
  const [isTrackingStopped, setIsTrackingStopped] = useState(false);
  const [refreshAttempt, setRefreshAttempt] = useState(0);
  const [restartKey, setRestartKey] = useState(0);
  const currentRef = useRef<PlaybackSessionTracker | null>(null);
  const shouldAutoPlayRef = useRef(true);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  videoElementRef.current = video;

  useEffect(() => {
    if (contentItemId === null) {
      return undefined;
    }

    let isCancelled = false;
    startSession({ contentItemId })
      .unwrap()
      .then((started) => {
        if (isCancelled) {
          endSession({ sessionId: started.sessionId, certificationSlug });
          return;
        }
        const next: PlaybackSessionTracker = {
          session: started,
          sequence: 1,
          spanStart: started.resumePositionSeconds,
          lastTime: started.resumePositionSeconds,
          playbackRate: 1,
          inFlight: null,
          hasPendingFlush: false,
          retryAt: 0,
          retryDelayMs: HEARTBEAT_RETRY_MS,
          isClosed: false,
          isEndedOnPageHide: false,
          restore: null,
        };
        currentRef.current = next;
        setTracker(next);
        setPlaybackUrl(started.playbackUrl);
        setExpiresAt(started.playbackUrlExpiresAt);
        setRefreshAttempt(0);
        setIsTrackingStopped(false);
        setProgress({
          coveredPercent: Number(started.coveredPercent),
          isComplete: started.isComplete,
        });
      })
      .catch(() => undefined);

    return () => {
      isCancelled = true;
      currentRef.current = null;
      setTracker(null);
      setPlaybackUrl(null);
      setExpiresAt(null);
      setProgress(null);
      setIsTrackingStopped(false);
    };
  }, [contentItemId, restartKey, certificationSlug, startSession, endSession]);

  const sendSpan = useCallback(
    function send(current: PlaybackSessionTracker): Promise<void> {
      if (current.inFlight) {
        current.hasPendingFlush = true;
        return current.inFlight;
      }
      const { session } = current;
      const sentFrom = current.spanStart;
      const fromSeconds = Math.floor(sentFrom);
      const maxSpanSeconds =
        session.heartbeatIntervalSeconds * Number(session.maxPlaybackRate);
      const toSeconds = Math.min(
        Math.floor(current.lastTime),
        fromSeconds + maxSpanSeconds,
        session.durationSeconds,
      );
      if (
        current.isClosed ||
        toSeconds <= fromSeconds ||
        Date.now() < current.retryAt
      ) {
        return Promise.resolve();
      }

      const backOff = () => {
        current.retryAt = Date.now() + current.retryDelayMs;
        current.retryDelayMs = Math.min(
          current.retryDelayMs * 2,
          MAX_HEARTBEAT_RETRY_MS,
        );
      };

      current.inFlight = sendHeartbeat({
        sessionId: session.sessionId,
        sequence: current.sequence,
        fromSeconds,
        toSeconds,
        playbackRate: current.playbackRate,
        clientTimestamp: new Date().toISOString(),
      })
        .unwrap()
        .then((response) => {
          const isCurrent = current === currentRef.current;
          if (response.nextSequence !== null) {
            current.sequence = response.nextSequence;
          }
          if (response.rejectReason === HEARTBEAT_REJECT_REASONS.RATE_LIMITED) {
            backOff();
          } else {
            current.retryAt = 0;
            current.retryDelayMs = HEARTBEAT_RETRY_MS;
            if (current.spanStart === sentFrom) {
              current.spanStart = toSeconds;
            }
          }
          if (
            response.rejectReason === HEARTBEAT_REJECT_REASONS.SESSION_CLOSED
          ) {
            current.isClosed = true;
            if (isCurrent) {
              setIsTrackingStopped(true);
            }
          }
          if (
            isCurrent &&
            response.coveredPercent !== null &&
            response.isComplete !== null
          ) {
            setProgress({
              coveredPercent: Number(response.coveredPercent),
              isComplete: response.isComplete,
            });
          }
        })
        .catch(backOff)
        .finally(() => {
          current.inFlight = null;
          if (current.hasPendingFlush) {
            current.hasPendingFlush = false;
            void send(current);
          }
        });

      return current.inFlight;
    },
    [sendHeartbeat],
  );

  useEffect(() => {
    if (!tracker || !video) {
      return undefined;
    }
    const { session } = tracker;
    const maxRate = Number(session.maxPlaybackRate);

    const onLoadedMetadata = () => {
      const { restore } = tracker;
      tracker.restore = null;
      if (restore) {
        video.currentTime = restore.time;
        video.playbackRate = restore.playbackRate;
        if (restore.isPlaying) {
          void video.play().catch(() => undefined);
        }
        return;
      }
      const resumeAt = Math.max(
        tracker.lastTime,
        session.resumePositionSeconds,
      );
      if (resumeAt < video.duration - RESUME_END_MARGIN_SECONDS) {
        video.currentTime = resumeAt;
      } else {
        tracker.spanStart = 0;
        tracker.lastTime = 0;
      }
      if (shouldAutoPlayRef.current) {
        void video.play().catch(() => undefined);
      }
      shouldAutoPlayRef.current = true;
    };
    const onTimeUpdate = () => {
      if (video.seeking) {
        return;
      }
      tracker.lastTime = video.currentTime;
      if (
        tracker.lastTime - tracker.spanStart >=
        session.heartbeatIntervalSeconds
      ) {
        void sendSpan(tracker);
      }
    };
    const onSeeking = () => {
      void sendSpan(tracker);
      tracker.spanStart = video.currentTime;
      tracker.lastTime = video.currentTime;
    };
    const onPause = () => {
      void sendSpan(tracker);
    };
    const onRateChange = () => {
      if (video.playbackRate > maxRate) {
        video.playbackRate = maxRate;
      }
      tracker.playbackRate = Math.round(video.playbackRate * 100) / 100;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        void sendSpan(tracker);
      }
    };

    onRateChange();
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("seeking", onSeeking);
    video.addEventListener("pause", onPause);
    video.addEventListener("ratechange", onRateChange);
    document.addEventListener("visibilitychange", onVisibilityChange);
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
      onLoadedMetadata();
    }

    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("seeking", onSeeking);
      video.removeEventListener("pause", onPause);
      video.removeEventListener("ratechange", onRateChange);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [tracker, video, sendSpan]);

  useEffect(() => {
    if (
      !tracker ||
      !expiresAt ||
      tracker.isClosed ||
      refreshAttempt > MAX_URL_REFRESH_ATTEMPTS
    ) {
      return undefined;
    }
    const delay =
      refreshAttempt > 0
        ? URL_REFRESH_RETRY_MS * refreshAttempt
        : Math.max(
            new Date(expiresAt).getTime() - Date.now() - URL_REFRESH_LEAD_MS,
            MIN_URL_REFRESH_DELAY_MS,
          );
    const timeout = window.setTimeout(() => {
      refreshUrl({ sessionId: tracker.session.sessionId })
        .unwrap()
        .then((signed) => {
          if (tracker !== currentRef.current) {
            return;
          }
          const player = videoElementRef.current;
          void sendSpan(tracker);
          tracker.restore = player
            ? {
                time: player.currentTime,
                isPlaying: !player.paused,
                playbackRate: player.playbackRate,
              }
            : null;
          setRefreshAttempt(0);
          setPlaybackUrl(signed.playbackUrl);
          setExpiresAt(signed.playbackUrlExpiresAt);
        })
        .catch(() => setRefreshAttempt((attempt) => attempt + 1));
    }, delay);

    return () => window.clearTimeout(timeout);
  }, [tracker, expiresAt, refreshAttempt, refreshUrl, sendSpan]);

  useEffect(() => {
    if (!tracker) {
      return undefined;
    }
    const { sessionId } = tracker.session;

    const onPageHide = () => {
      if (tracker.isClosed) {
        return;
      }
      tracker.isClosed = true;
      tracker.isEndedOnPageHide = true;
      shouldAutoPlayRef.current = Boolean(
        videoElementRef.current && !videoElementRef.current.paused,
      );
      endPlaybackSessionOnUnload({
        sessionId,
        lastPositionSeconds: Math.floor(tracker.lastTime),
      });
    };
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted && tracker.isEndedOnPageHide) {
        setRestartKey((key) => key + 1);
      }
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      if (tracker.isClosed) {
        return;
      }
      const settle = async () => {
        while (tracker.inFlight) {
          await tracker.inFlight;
        }
      };
      void (async () => {
        await settle();
        tracker.retryAt = 0;
        await sendSpan(tracker);
        await settle();
        tracker.isClosed = true;
        endSession({
          sessionId,
          certificationSlug,
          lastPositionSeconds: Math.floor(tracker.lastTime),
        });
      })();
    };
  }, [tracker, sendSpan, endSession, certificationSlug]);

  return {
    videoRef: setVideo,
    playbackUrl,
    progress,
    isStarting: contentItemId !== null && !tracker && !isError,
    isError: isError || refreshAttempt > MAX_URL_REFRESH_ATTEMPTS,
    isTrackingStopped,
  };
};
